import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet } from 'react-router-dom';
import {
    AppBar,
    Box,
    CssBaseline,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    useTheme,
    useMediaQuery,
    Divider,
    alpha,
    Avatar,
    Button,
    Tooltip,
    Menu,
    MenuItem,
    Collapse,
    Chip,
    BottomNavigation,
    BottomNavigationAction
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BuildIcon from '@mui/icons-material/Build';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'; // Admin icon
import SecurityIcon from '@mui/icons-material/Security'; // Safety icon
import AssignmentIcon from '@mui/icons-material/Assignment'; // Forms icon
import PaidIcon from '@mui/icons-material/Paid';
import BusinessIcon from '@mui/icons-material/Business'; // Company management icon
import PersonAddIcon from '@mui/icons-material/PersonAdd'; // Recruitment icon
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'; // Quote management icon
import FactCheckIcon from '@mui/icons-material/FactCheck'; // Forms management icon
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Badge from '@mui/material/Badge';
import { Logout, AccountCircle, Settings, Person, RateReview } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import UserProfileForm from './UserProfileForm';
import VehicleTypeSelector from './VehicleTypeSelector';
import apiClient from '../api';
import { useLocation } from 'react-router-dom';
import samsaraPollingService from '../services/samsaraPollingService';
import { isFieldDriver } from '../utils/personnelType';
import { isServiceRole } from '../utils/serviceTickets';
import MobileBottomBar from './MobileBottomBar';


const drawerWidth = 240; // Width of the sidebar
const BOL_UPLOAD_URL = 'https://tms-kexv.loadtracking.com/dtalgx-ImgCapture/';

function UploadBolButton() {
    return (
        <Button
            component="a"
            href={BOL_UPLOAD_URL}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            sx={{
                ml: 'auto',
                fontWeight: 800,
                color: '#1c1917',
                bgcolor: '#FFE14D',
                whiteSpace: 'nowrap',
                px: 1.5,
                boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
                '&:hover': { bgcolor: '#FFD000' },
            }}
        >
            Upload BOL
        </Button>
    );
}

// Base menu items for all users
const menuItems = [
    { text: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
    { text: 'Customers', path: '/customers', icon: <PeopleIcon /> },
    { text: 'Truckbook', path: '/trucks', icon: <LocalShippingIcon /> },
    { text: 'Safety', path: '/safety', icon: <SecurityIcon /> },
    { text: 'Forms Center', path: '/forms', icon: <AssignmentIcon /> },
    { text: 'Referral', path: '/referrals', icon: <PaidIcon sx={{ color: '#2e7d32 !important' }} />, accent: '#2e7d32' },
];

// Admin-only menu item
const adminMenuItem = { text: 'Admin Portal', path: '/admin', icon: <AdminPanelSettingsIcon /> };

// Company admin menu item
const companyAdminMenuItem = { text: 'Company Management', path: '/admin', icon: <BusinessIcon /> };

// Add a constant for the last regular menu item text to use for comparison
const LAST_REGULAR_MENU_ITEM = 'Referral';

function linkIsActive(path, loc) {
    const splitAt = path.indexOf('?');
    const pathname = splitAt === -1 ? path : path.slice(0, splitAt);
    const search = splitAt === -1 ? '' : path.slice(splitAt + 1);
    if (pathname === '/forms-management') {
        if (loc.pathname !== '/forms-management') return false;
        const wanted = new URLSearchParams(search).get('tab') || '0';
        const current = new URLSearchParams(loc.search).get('tab') || '0';
        return wanted === current;
    }
    return loc.pathname === pathname || loc.pathname.startsWith(`${pathname}/`);
}

function driverBarValue(pathname) {
    if (pathname.startsWith('/forms/weekly-checklist')) return 0;
    if (pathname.startsWith('/forms/time-off')) return 1;
    if (pathname.startsWith('/forms/apparel')) return 2;
    if (pathname.startsWith('/trucks') || pathname.startsWith('/truck-detail')) return 3;
    return false;
}

function Layout() {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
    const [mobileOpen, setMobileOpen] = useState(false);
    const [sideOpen, setSideOpen] = useState(true);
    const { user, logout, isAuthenticated, updateUserProfile } = useAuth();
    const [profileFormOpen, setProfileFormOpen] = useState(() => {
        // Initialize from localStorage to survive re-renders
        return localStorage.getItem('hl_profile_modal_open') === 'true';
    });
    
    // Debug wrapper for setProfileFormOpen
    const setProfileFormOpenDebug = (value) => {
        console.log('[Layout] setProfileFormOpen called with value:', value);
        console.trace('[Layout] Stack trace for setProfileFormOpen');
        
        // Persist to localStorage
        if (value) {
            localStorage.setItem('hl_profile_modal_open', 'true');
        } else {
            localStorage.removeItem('hl_profile_modal_open');
        }
        
        setProfileFormOpen(value);
    };
    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    // Route-based control for Samsara polling to avoid thrash
    useEffect(() => {
        let timeoutId;
        const canUseSamsara = user?.role === 'admin' || user?.role === 'company_admin';
        const shouldResume = canUseSamsara && (location.pathname.startsWith('/trucks') || location.pathname.startsWith('/safety'));
        if (shouldResume) {
            timeoutId = setTimeout(() => samsaraPollingService.resumeAllPolling(), 100);
        } else {
            samsaraPollingService.pauseAllPolling();
        }
        return () => { if (timeoutId) clearTimeout(timeoutId); };
    }, [location.pathname, user?.role]);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [moreOpen, setMoreOpen] = useState(() => {
        const saved = localStorage.getItem('hl_more_open');
        return saved === 'true';
    });

    // Badge counts for recruitment, quotes, and forms
    const [badgeCounts, setBadgeCounts] = useState({ resumes: 0, quotes: 0, timeOff: 0, apparel: 0, anomalies: 0 });

    // Check user roles
    const isAdmin = user && user.role === 'admin';
    const isCompanyAdmin = user && user.role === 'company_admin';
    const isService = isServiceRole(user?.role);

    useEffect(() => {
        if (!isService) return;
        const path = location.pathname;
        if (path === '/dashboard' || path === '/shop' || path.startsWith('/shop/')) return;
        navigate('/shop', { replace: true });
    }, [isService, location.pathname, navigate]);

    // Fetch unviewed counts for badges (company admin / platform admin only)
    React.useEffect(() => {
        if (isService || (!isAdmin && !isCompanyAdmin)) return;

        const fetchBadgeCounts = async () => {
            try {
                const [resumesRes, quotesRes, timeOffRes, apparelRes, anomalyRes] = await Promise.all([
                    apiClient.get('/intake-admin/resumes', { params: { page: 1, limit: 1 } }),
                    apiClient.get('/intake-admin/quotes', { params: { page: 1, limit: 1 } }),
                    apiClient.get('/forms/time-off'),
                    apiClient.get('/forms/apparel'),
                    apiClient.get('/anomalies/count').catch(() => ({ data: { count: 0 } })),
                ]);
                const r = resumesRes?.data?.data?.unviewed || 0;
                const q = quotesRes?.data?.data?.unviewed || 0;
                
                // Count pending forms
                const timeOffData = timeOffRes?.data?.data || [];
                const apparelData = apparelRes?.data?.data || [];
                const pendingTimeOff = timeOffData.filter(req => req.status === 'pending').length;
                const pendingApparel = apparelData.filter(order => order.status === 'submitted').length;
                const anomalies = anomalyRes?.data?.count || 0;
                
                setBadgeCounts({
                    resumes: r,
                    quotes: q,
                    timeOff: pendingTimeOff,
                    apparel: pendingApparel,
                    anomalies,
                });
            } catch (err) {
                // Silent fail - badges just won't show
            }
        };

        let interval = null;
        const start = () => {
            fetchBadgeCounts();
            if (interval) clearInterval(interval);
            interval = setInterval(fetchBadgeCounts, 30000);
        };
        const stop = () => {
            if (interval) clearInterval(interval);
            interval = null;
        };
        const onVisibility = () => {
            if (document.hidden) stop();
            else start();
        };

        if (!document.hidden) start();
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            stop();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [isAdmin, isCompanyAdmin, isService]);

    // Get menu items based on user role
    const showDriverBar = isMobile && user?.role === 'company_user';

    const reviewerMenu = () => ([
        { text: 'Time off', path: '/forms-management?tab=0', icon: <ScheduleIcon />, badge: badgeCounts.timeOff },
        { text: 'Recruitment', path: '/recruitment', icon: <PersonAddIcon />, badge: badgeCounts.resumes },
        { text: 'Checklists', path: '/forms-management?tab=2', icon: <FactCheckIcon /> },
        { text: 'Needs a look', path: '/needs-a-look', icon: <ReportProblemOutlinedIcon />, badge: badgeCounts.anomalies },
        { text: 'Request time off', path: '/forms/time-off', icon: <EventAvailableIcon /> },
        { text: 'Order apparel', path: '/forms/apparel', icon: <CheckroomIcon /> },
        { text: 'Quotes', path: '/quotes', icon: <RequestQuoteIcon />, badge: badgeCounts.quotes },
        { text: 'Customers', path: '/customers', icon: <PeopleIcon />, dividerBefore: true, tour: 'nav-customers' },
        { text: 'Truckbook', path: '/trucks', icon: <LocalShippingIcon />, tour: 'nav-truckbook' },
        { text: 'Tickets', path: '/shop', icon: <BuildIcon /> },
        isAdmin ? { ...adminMenuItem, tour: undefined } : { ...companyAdminMenuItem, tour: 'nav-company-management' },
    ]);

    const moreMenu = () => ([
        { text: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
        { text: 'Safety', path: '/safety', icon: <SecurityIcon /> },
        { text: 'Apparel orders', path: '/forms-management?tab=1', icon: <LocalMallIcon />, badge: badgeCounts.apparel },
    ]);

    const moreOpenForPage = moreMenu().some((item) => linkIsActive(item.path, location));

    useEffect(() => {
        if (moreOpenForPage) setMoreOpen(true);
    }, [location.pathname, location.search]);

    const compactNav = (isAdmin || isCompanyAdmin || isService) && !showDriverBar;

    const serviceMenu = () => ([
        { text: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
        { text: 'Tickets', path: '/shop', icon: <BuildIcon /> },
    ]);

    const getMenuItems = () => {
        if (isService) return serviceMenu();
        if (isAdmin || isCompanyAdmin) return reviewerMenu();
        return menuItems;
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const showDesktopBar = !isMobile && isFieldDriver(user);

    // Navigate to dashboard when logo is clicked
    const handleLogoClick = () => {
        navigate('/');
        if (isMobile) {
            setMobileOpen(false);
        }
    };

    // Handle user menu
    const handleUserMenuOpen = (event) => {
        setUserMenuAnchor(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchor(null);
    };

    // Handle profile form
    const handleProfileOpen = () => {
        setUserMenuAnchor(null);
        setProfileFormOpenDebug(true);
    };

    const handleProfileClose = () => {
        console.log('[Layout] handleProfileClose called - closing profile modal');
        setProfileFormOpenDebug(false);
    };
    
    // Handle profile save - this calls the updateUserProfile method from AuthContext
    const handleProfileSave = async (updatedProfileData) => {
        console.log('Updated profile data:', updatedProfileData);
        setFormSubmitting(true);
        
        try {
            // Call the API using the context method
            const result = await updateUserProfile(updatedProfileData);
            
            console.log('[Layout] handleProfileSave result:', result);
            
            if (result && result.success) {
                // Close the form on success
                console.log('[Layout] Profile save successful - closing modal');
                setProfileFormOpenDebug(false);
            } else {
                console.log('[Layout] Profile save failed - keeping modal open for error display');
                // Explicitly ensure modal stays open for errors
                setProfileFormOpenDebug(true);
            }
            
            return result;
        } catch (error) {
            console.error('Error updating profile:', error);
            // Keep modal open for errors
            setProfileFormOpenDebug(true);
            return { 
                success: false, 
                error: error.message || 'Failed to update profile' 
            };
        } finally {
            setFormSubmitting(false);
        }
    };

    const drawerContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: 'background.paper' }}>
            {/* Logo Area */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: compactNav ? 0.5 : 1,
                    px: 1.5,
                    cursor: 'pointer',
                }}
                onClick={handleLogoClick}
            >
                <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}/portal-logo.png`}
                    alt="Kranawetter Transport"
                    sx={{ width: '78%', maxHeight: 46, height: 'auto', display: 'block' }}
                />
            </Box>
            <Divider />
            {/* Navigation List */}
            <List sx={{ py: compactNav ? 0 : 1, flexGrow: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                {getMenuItems().map((item, index, array) => (
                    <React.Fragment key={item.text}>
                        {item.dividerBefore && <Divider sx={{ my: compactNav ? 0.5 : 1.5, mx: 2 }} />}
                        <ListItem disablePadding sx={{ display: 'block', mb: compactNav ? 0 : 0.5 }}> {/* Added margin bottom */} 
                            <ListItemButton
                                component={Link}
                                to={item.path}
                                selected={linkIsActive(item.path, location)}
                                aria-current={linkIsActive(item.path, location) ? 'page' : undefined}
                                onClick={() => { if (isMobile) handleDrawerToggle(); }}
                                data-tour-id={
                                    item.tour
                                    || (item.text === 'Customers' ? 'nav-customers'
                                    : item.text === 'Truckbook' ? 'nav-truckbook'
                                    : item.text === 'Company Management' ? 'nav-company-management'
                                    : undefined)
                                }
                                sx={{
                                    minHeight: compactNav ? 36 : 48,
                                    justifyContent: 'initial', // Keep content aligned left
                                    px: compactNav ? 1.5 : 2.5,
                                    borderRadius: theme.shape.borderRadius,
                                    mx: compactNav ? 1 : 1.5,
                                    color: '#E94F37',
                                    transition: 'all 0.2s',
                                    '& .MuiListItemIcon-root': {
                                        color: alpha('#E94F37', 0.7),
                                        transition: 'all 0.2s',
                                    },
                                    '& .MuiSvgIcon-root': {
                                        fontSize: compactNav ? 20 : 24,
                                    },
                                    '&.Mui-selected': {
                                        color: '#E94F37',
                                        backgroundColor: alpha('#E94F37', 0.1),
                                        fontWeight: 'fontWeightBold',
                                        position: 'relative',
                                        '& .MuiListItemIcon-root': {
                                            color: '#E94F37',
                                        },
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            left: 0,
                                            top: 6,
                                            bottom: 6,
                                            width: 3,
                                            borderRadius: 2,
                                            backgroundColor: '#E94F37'
                                        }
                                    },
                                    '&:hover': {
                                        backgroundColor: alpha('#E94F37', 0.05),
                                        color: '#E94F37',
                                        transform: 'translateX(3px)',
                                        '& .MuiListItemIcon-root': { color: '#E94F37' }
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ 
                                    minWidth: compactNav ? 28 : 0,
                                    mr: compactNav ? 1.5 : 3, // Space between icon and text
                                    justifyContent: 'center',
                                    color: 'inherit' // Inherit color will now pick up the parent's #E94F37
                                 }}>
                                    {item.badge > 0 ? (
                                        <Badge badgeContent={item.badge} color="error" max={99}>
                                            {item.icon}
                                        </Badge>
                                    ) : item.icon}
                                </ListItemIcon>
                                <ListItemText 
                                    primary={item.text} 
                                    primaryTypographyProps={{
                                        fontSize: compactNav ? '0.875rem' : '0.95rem',
                                        lineHeight: 1.2,
                                        noWrap: compactNav,
                                        sx: item.accent ? { color: item.accent, fontWeight: 800 } : undefined,
                                    }}
                                    sx={{ opacity: 1 }} 
                                />
                            </ListItemButton>
                        </ListItem>
                        {/* Add a divider after the Safety menu item */}
                        {item.text === LAST_REGULAR_MENU_ITEM && index < array.length - 1 && (
                            <Divider sx={{ my: 1.5, mx: 2 }} />
                        )}
                    </React.Fragment>
                ))}
                
                {(isAdmin || isCompanyAdmin) && (
                    <>
                        <ListItem disablePadding sx={{ display: 'block' }}>
                            <ListItemButton
                                onClick={() => {
                                    setMoreOpen((open) => {
                                        const next = !open;
                                        try { localStorage.setItem('hl_more_open', String(next)); } catch (_) {}
                                        return next;
                                    });
                                }}
                                sx={{
                                    minHeight: 36,
                                    px: 1.5,
                                    borderRadius: theme.shape.borderRadius,
                                    mx: 1,
                                    color: '#E94F37',
                                    transition: 'all 0.2s',
                                    '& .MuiListItemIcon-root': {
                                        color: alpha('#E94F37', 0.7),
                                        transition: 'all 0.2s',
                                    },
                                    '& .MuiSvgIcon-root': { fontSize: 20 },
                                    '&:hover': {
                                        backgroundColor: alpha('#E94F37', 0.05),
                                        color: '#E94F37',
                                        transform: 'translateX(3px)',
                                        '& .MuiListItemIcon-root': {
                                            color: '#E94F37',
                                        }
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: 'center' }}>
                                    {badgeCounts.apparel > 0 ? (
                                        <Badge badgeContent={badgeCounts.apparel} color="error" max={99}>
                                            <MoreHorizIcon />
                                        </Badge>
                                    ) : <MoreHorizIcon />}
                                </ListItemIcon>
                                <ListItemText primary="More" primaryTypographyProps={{ fontSize: '0.875rem', lineHeight: 1.2, noWrap: true }} />
                                {moreOpen ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                        </ListItem>
                        <Collapse in={moreOpen} timeout="auto" unmountOnExit onEntered={(node) => node.scrollIntoView({ block: 'nearest' })}>
                            <List component="div" disablePadding>
                                {moreMenu().map((item) => (
                                    <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                                        <ListItemButton
                                            component={Link}
                                            to={item.path}
                                            selected={linkIsActive(item.path, location)}
                                            aria-current={linkIsActive(item.path, location) ? 'page' : undefined}
                                            onClick={() => { if (isMobile) handleDrawerToggle(); }}
                                            sx={{
                                                minHeight: 36,
                                                pl: 5,
                                                pr: 2.5,
                                                borderRadius: theme.shape.borderRadius,
                                                mx: 1.5,
                                                my: 0.125,
                                                color: alpha('#E94F37', 0.8),
                                                transition: 'all 0.2s',
                                                '& .MuiListItemIcon-root': {
                                                    color: alpha('#E94F37', 0.6),
                                                    transition: 'all 0.2s',
                                                },
                                                '& .MuiSvgIcon-root': { fontSize: 18 },
                                                '&.Mui-selected': {
                                                    color: '#E94F37',
                                                    backgroundColor: alpha('#E94F37', 0.1),
                                                    fontWeight: 'fontWeightBold',
                                                    position: 'relative',
                                                    '& .MuiListItemIcon-root': { color: '#E94F37' },
                                                    '&::before': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        left: 0,
                                                        top: 6,
                                                        bottom: 6,
                                                        width: 3,
                                                        borderRadius: 2,
                                                        backgroundColor: '#E94F37'
                                                    }
                                                },
                                                '&:hover': {
                                                    backgroundColor: alpha('#E94F37', 0.05),
                                                    color: '#E94F37',
                                                    transform: 'translateX(3px)',
                                                    '& .MuiListItemIcon-root': { color: '#E94F37' }
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: 'center' }}>
                                                {item.badge > 0 ? (
                                                    <Badge badgeContent={item.badge} color="error" max={99}>
                                                        {item.icon}
                                                    </Badge>
                                                ) : item.icon}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item.text}
                                                primaryTypographyProps={{ fontSize: '0.8125rem', lineHeight: 1.3 }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Collapse>
                    </>
                )}
            </List>
            {/* User Info & Logout */}
            <Box sx={{ flexShrink: 0, bgcolor: 'background.paper', position: 'relative', zIndex: 1 }}>
                 <Divider />
                 {isAuthenticated && user && (
                     <Box sx={{ 
                        py: compactNav ? 1 : 2,
                        px: compactNav ? 1.5 : 2,
                        display: 'flex', 
                        alignItems: 'center',
                        backgroundColor: alpha(theme.palette.primary.main, 0.03),
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        }
                     }}
                     onClick={handleUserMenuOpen}
                     >
                        <Avatar sx={{ 
                            bgcolor: 'secondary.main', 
                            width: 32, 
                            height: 32, 
                            mr: 1.5,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                            {user.name ? user.name.charAt(0).toUpperCase() : <AccountCircle />}
                        </Avatar>
                        <Box sx={{ overflow: 'hidden' }}>
                            <Typography variant="body2" noWrap fontWeight="bold">
                                {user.name}
                            </Typography>
                            <Typography variant="caption" noWrap color="text.secondary">
                                {user.role === 'admin' ? 'Administrator' :
                                 user.role === 'company_admin' ? 'Company Admin' :
                                 user.role === 'service_admin' ? 'Service Admin' :
                                 user.role === 'service_viewer' ? 'Service' :
                                 (user.company && typeof user.company === 'object' && user.company.name) ? 
                                     user.company.name : 'Company User'}
                            </Typography>
                        </Box>
                        <IconButton 
                            aria-label="settings"
                            size="small"
                            sx={{ ml: 'auto' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleUserMenuOpen(e);
                            }}
                        >
                            <Settings fontSize="small" />
                        </IconButton>
                     </Box>
                 )}

                 {/* User Menu */}
                 <Menu
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    onClose={handleUserMenuClose}
                    PaperProps={{
                        elevation: 3,
                        sx: { minWidth: 180 }
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                 >
                    <MenuItem onClick={handleProfileOpen} dense>
                        <ListItemIcon>
                            <Person fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Edit Profile" />
                    </MenuItem>
                    {!isService && (
                    <MenuItem
                        onClick={() => {
                            handleUserMenuClose();
                            navigate('/feedback');
                        }}
                        dense
                    >
                        <ListItemIcon>
                            <RateReview fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Feedback" />
                    </MenuItem>
                    )}
                    <Divider />
                    <MenuItem onClick={logout} dense>
                        <ListItemIcon>
                            <Logout fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText primary="Logout" sx={{ color: 'error.main' }} />
                    </MenuItem>
                 </Menu>
            </Box>
        </Box>
    );

    const sideWidth = isMobile || (showDesktopBar && !sideOpen) ? 0 : drawerWidth;

    return (
        <Box sx={{ display: 'flex', width: '100%', minWidth: 0 }}>
            <CssBaseline />
            {/* AppBar only shown on mobile to contain the toggle button */}
            {isMobile && (
                 <AppBar
                    position="fixed"
                    elevation={1} // Slight elevation for better visibility
                    sx={{
                        width: '100%',
                        zIndex: theme.zIndex.drawer + 1,
                        backgroundColor: 'white',
                        color: 'text.primary'
                    }}
                >
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ mr: 2 }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography
                            variant="subtitle2"
                            onClick={() => navigate('/')}
                            sx={{
                                fontWeight: 900,
                                letterSpacing: 1,
                                textTransform: 'uppercase',
                                lineHeight: 1,
                                cursor: 'pointer',
                                userSelect: 'none',
                                fontFamily: 'Oswald, Arial, Helvetica, sans-serif'
                            }}
                        >
                            <Box component="span" sx={{ color: '#1c1917', fontWeight: 900 }}>Kranawetter Driver Portal</Box>
                        </Typography>
                        {isFieldDriver(user) && <UploadBolButton />}
                    </Toolbar>
                </AppBar>
            )}

            {!isMobile && isFieldDriver(user) && (
                <AppBar
                    position="fixed"
                    elevation={1}
                    sx={{
                        width: sideOpen ? `calc(100% - ${drawerWidth}px)` : '100%',
                        ml: sideOpen ? `${drawerWidth}px` : 0,
                        backgroundColor: 'white',
                        color: 'text.primary',
                        transition: theme.transitions.create(['width', 'margin'], { duration: theme.transitions.duration.short }),
                    }}
                >
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            aria-label={sideOpen ? 'Hide menu' : 'Show menu'}
                            edge="start"
                            onClick={() => setSideOpen((open) => !open)}
                            sx={{ mr: 2 }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography
                            variant="subtitle2"
                            onClick={() => navigate('/')}
                            sx={{
                                fontWeight: 900,
                                letterSpacing: 1,
                                textTransform: 'uppercase',
                                lineHeight: 1,
                                cursor: 'pointer',
                                userSelect: 'none',
                                fontFamily: 'Oswald, Arial, Helvetica, sans-serif',
                            }}
                        >
                            <Box component="span" sx={{ color: '#1c1917', fontWeight: 900 }}>Kranawetter Driver Portal</Box>
                        </Typography>
                        <UploadBolButton />
                    </Toolbar>
                </AppBar>
            )}

            {isMobile && (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            borderRight: 'none',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}

            {!isMobile && (
            <Box
                component="nav"
                sx={{
                    width: sideWidth,
                    flex: '0 0 auto',
                    overflow: 'hidden',
                    transition: theme.transitions.create('width', { duration: theme.transitions.duration.short }),
                }}
                aria-label="main navigation"
            >
                <Drawer
                    variant="permanent"
                    sx={{
                        display: sideWidth ? 'block' : 'none',
                        '& .MuiDrawer-paper': { 
                            boxSizing: 'border-box', 
                            width: drawerWidth, 
                            borderRight: 'none',
                            boxShadow: '2px 0 8px rgba(0,0,0,0.05)'
                          },
                    }}
                    open
                >
                    {drawerContent}
                </Drawer>
            </Box>
            )}

            {/* Main Content Area */}
            <Box
                component="main"
                sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    width: sideWidth ? `calc(100% - ${sideWidth}px)` : '100%',
                    mt: (isMobile || isFieldDriver(user)) ? '64px' : 0,
                    pb: showDriverBar ? 'calc(96px + env(safe-area-inset-bottom, 0px))' : 0,
                    backgroundColor: theme.palette.background.default, // Use theme background
                    minHeight: '100vh', // Ensure content area fills height
                    transition: 'width 0.3s ease'
                }}
            >
                {!isService && <VehicleTypeSelector />}

                {isAdmin && user?.readStore === 'dynamo' && (
                    <Box sx={{ px: 3, pt: 2 }}>
                        <Chip
                            color="warning"
                            label="Live reads are DynamoDB. Writes still go to Mongo and Dynamo. Turn off in Admin → Data Management to fall back to Mongo."
                            sx={{ fontWeight: 700 }}
                        />
                    </Box>
                )}
                
                {/* Page Content */}
                <Box sx={{
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    overflowX: 'hidden',
                    px: (isMobile && location.pathname.includes('/forms/weekly-checklist') && isFieldDriver(user)) ? 1 : 1.5,
                    pt: 1,
                    pb: (isMobile && location.pathname.includes('/forms/weekly-checklist') && isFieldDriver(user)) ? 1 : 3
                }}>
                    <Outlet />
                </Box>
            </Box>

            {showDriverBar && (
                <MobileBottomBar>
                    <BottomNavigation
                        showLabels
                        value={driverBarValue(location.pathname)}
                        onChange={(event, next) => {
                            const destinations = ['/forms/weekly-checklist', '/forms/time-off', '/forms/apparel', '/trucks'];
                            navigate(destinations[next]);
                        }}
                    >
                        <BottomNavigationAction label="Weekly" icon={<ChecklistRtlIcon />} />
                        <BottomNavigationAction label="Time off" icon={<ScheduleIcon />} />
                        <BottomNavigationAction label="Apparel" icon={<RequestQuoteIcon />} />
                        <BottomNavigationAction label="Truckbook" icon={<LocalShippingIcon />} />
                    </BottomNavigation>
                </MobileBottomBar>
            )}

            {/* User Profile Form */}
            {user && (
                <UserProfileForm
                    open={profileFormOpen}
                    onClose={handleProfileClose}
                    onSave={handleProfileSave}
                    user={user}
                />
            )}


        </Box>
    );
}

export default Layout; 